import Service, { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import { ObjectId } from 'bson';
import config from 'cpc-license-manager/config/environment';
import { userRoles } from 'cpc-license-manager/constants/user-roles';

export default class CurrentUser extends Service {
  @service session;
  @service store;
  @service userRepo;

  @tracked user;

  tabSession = new ObjectId().toString();

  get isAdmin() {
    return this.user?.isAdmin;
  }

  get isUser() {
    return this.user?.isUser;
  }

  get language() {
    return this.user?.language;
  }

  get isNotUser() {
    return !this.isUser;
  }

  get isNotAdmin() {
    return this.user?.isNotAdmin;
  }

  get id() {
    return this.user?.id;
  }

  setupUser = task(waitFor(async () => {
    const idToken = this.session.data?.authenticated?.id_token;

    if (!this.session.data?.authenticated?.userId && !idToken) {
      return;
    }

    try {
      if (idToken) {
        this.user = await this._setupSsoUser.perform();
      } else {
        this.user = this.store.peekRecord('user', this.session.data?.authenticated?.userId);
      }
    } catch (e) {
      if (!didCancel(e)) {
        console.error(e);
        await this.session.invalidate();
      }
    }
  }));

  _setupSsoUser = task(waitFor(async () => {
    const userName = this.session.data?.authenticated?.userinfo?.[config['ember-simple-auth-oidc'].userNameProperty];

    if (!userName) {
      await this.session.invalidate();
      return;
    }

    let user = this.userRepo.users.find(item => item.userName === userName);

    if (!user) {
      user = this.store.createRecord('user', {
        userName,
        isSso: true,
        role: userRoles.User.label,
      });

      await user.save();
    }

    return user;
  }));

  getUserTokenUrl(url) {
    if (url == null) {
      return null;
    }

    const token = this.session.token;
    const delimiter = url?.includes('?') ? '&' : '?';

    return `${url}${delimiter}jwt=${token}`;
  }

  hasPermission = (permission) => {
    return this.user.hasPermission(permission);
  };
}
